/* ══════════════════════════════════════════════════════════════════
   Der kleine Zeichensatz — fürs Telefon und für die zwei Werkzeuge im Kopf.

   **Am Schreibtisch gibt es keine Symbole in der Navigation**, und das
   bleibt so (28-designkonzept.md, „Navigation"): Ein Piktogramm für
   „Vorgänge" müsste erst gelernt werden und sagt danach nichts, was das
   Wort nicht schon sagt. Dort steht neben jedem Punkt genug Platz für
   das Wort.

   **Die Ausnahme sind die zwei Werkzeuge in der Kopfzeile** (18.09.2026):
   Suche und Nachschlagen tragen auch am breiten Schirm nur ihr Zeichen.
   Ein Navigationspunkt sagt, wohin man kommt — da hilft das Wort. Diese
   beiden tun etwas, und ihre Wörter standen nebeneinander und waren
   dieselbe Auskunft: „Suchen" und „Nachschlagen" heißt für den Leser
   zweimal „irgendwo nachsehen". Was sie unterscheidet, ist der Euro im
   einen Zeichen — nicht das Wort. Das Wort steht weiter da, nur nicht
   mehr gezeichnet: in `title` und `aria-label` (`schale/Schale.tsx`).

   **Am Telefon ist der Platz die Grenze.** Eine Leiste unten hat fünf
   Fächer auf 390 px — 78 px je Fach. Darin steht das Wort in 11 px, und
   darüber braucht das Auge etwas, das es beim Hinsehen trifft, ohne zu
   lesen. Das Zeichen ersetzt das Wort deshalb nicht, es steht darüber.

   Die Regeln, damit daraus ein Satz wird und keine Sammlung:

   * **Ein Stil.** Strich, kein Körper: `fill="none"`, 1,7 px Strich,
     runde Enden, 24er Raster. Kein gefülltes Zeichen dazwischen.
   * **`currentColor`.** Das Zeichen hat keine eigene Farbe — es nimmt
     die des Punktes, auf dem es steht, und kann damit hell wie dunkel.
   * **Keine Emoji, keine Bibliothek.** Emoji sehen auf jedem Gerät
     anders aus und lesen sich beim Vorlesen als Satz; eine Bibliothek
     brächte achthundert Zeichen für acht.
   * **Immer `aria-hidden`.** Das Wort daneben ist die Beschriftung. Ein
     Vorleser, der „Grafik" sagt und danach „Heute", sagt es zweimal.

   Kreise stehen als Bogen im Pfad und nicht als `<circle>`: So ist jedes
   Zeichen **eine** Liste von `d`-Angaben, und die Komponente bleibt eine
   Schleife statt einer Fallunterscheidung je Form.
   ══════════════════════════════════════════════════════════════════ */

/** Wie ein Kreis als Pfad aussieht — Mittelpunkt und Radius. */
function kreis(x: number, y: number, r: number): string {
  return `M${x + r} ${y}a${r} ${r} 0 1 1-${r * 2} 0a${r} ${r} 0 1 1${r * 2} 0`;
}

/**
 * Der Kalender — ein Blatt mit zwei Aufhängern und dem Kopf darunter.
 *
 * Er steht unter **zwei** Namen im Satz und ist trotzdem eine Zeichnung:
 * `heute` ist das Fach in der Handy-Leiste, `zeitraum` das Zeichen in jedem
 * Zeitraumfeld (`bausteine/Zeitraum.tsx`). Ein Name sagt, wofür etwas steht;
 * zwei Namen für dieselbe Sache sind billiger als zwei Pfadlisten, die beim
 * nächsten Strich auseinanderlaufen.
 */
const KALENDER = [
  "M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v12a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5Z",
  "M8 3v4", "M16 3v4", "M4 10h16",
];

/* ── Die beiden Werkzeuge im Kopf (18.09.2026) ─────────────────────
   Luca: *„Suche und Nachschlagen — das ist jetzt natürlich total
   irreführend, weil kein Mensch weiß, was er suchen soll und was er
   nachschlagen soll. Mach bitte da zwei kleine Icons hin … einmal diese
   Suche als Button und da so Nachschlagen. Man macht dann halt so eine
   Lupe mit einem Euro."*

   **Eine Lupe, zwei Zeichen.** Beide Werkzeuge sehen nach — das eine in
   den Belegen, das andere im Kalender und in der Preisliste. Deshalb
   dieselbe Lupe und als einziger Unterschied das, was darin liegt: beim
   Nachschlagen der Euro, denn es beantwortet die Preisfrage. Zwei
   verschiedene Lupen nebeneinander (die eine mit Liste, die andere
   nackt — so stand es den halben 18.09.) liest man als zwei Zeichen und
   muss beide lernen; eine Lupe mit und ohne Euro liest man als Paar.

   **Die Lupe ist größer als die vom 17.09.** (Kreis 7,2 statt 6,3). Das
   ist keine Laune: In eine Lupe mit Radius 6,3 passt bei 1,7 px Strich
   ein Euro, der bei 20 px zur Kugel verschmilzt — nachgemessen, indem
   beide Fassungen bei 16, 17, 20 und 24 px auf einen 2×-Schirm gerastert
   und pixelgenau vergrößert wurden. Und zwei Lupen unterschiedlicher
   Größe nebeneinander liest man als Versehen, also gilt das neue Maß für
   beide. Darum steht die Lupe hier einmal und nicht zweimal. */
const LUPE = [kreis(10.2, 10.2, 7.2), "m15.3 15.3 5.1 5.1"];

/* Der Euro als Strichform und nicht als `€` aus der Schrift: Ein `<text>`
   im Zeichen brächte eine zweite Art, ein Zeichen zu zeichnen, und sähe
   auf jedem Gerät nach der dort vorhandenen Schrift aus.

   Das C ist ein Bogen um (10,7 | 10,2) mit Radius 4,2, rechts auf ±56°
   offen; die beiden Balken liegen 1,3 über und unter der Mitte und ragen
   links über das C hinaus, wie beim gedruckten Zeichen. Jeder Punkt hält
   mindestens 1 Einheit Abstand zum Ring der Lupe — darunter fließen die
   Striche bei 20 px ineinander. */
const EURO = [
  "M13.05 6.72a4.2 4.2 0 1 0 0 6.96",
  "M6.2 8.9h5.2", "M6.2 11.5h5.2",
];

const PFADE = {
  /** Heute — der Kalender mit den beiden Aufhängern. */
  heute: KALENDER,
  /** Zeitraum — derselbe Kalender, am Feld statt an der Leiste. */
  zeitraum: KALENDER,
  /** Zeiten — die Uhr. Der Zeiger steht auf halb, nicht auf zwölf: sonst
      liest sich das Zeichen als Kreis mit einem Strich darin. */
  zeiten: [kreis(12, 12, 8.5), "M12 7.2v5l3.4 2"],
  /** Start — die Mappe des Verkäufers. */
  start: [
    "M3.5 8.5A1.5 1.5 0 0 1 5 7h14a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5Z",
    "M9 7V5.6A1.6 1.6 0 0 1 10.6 4h2.8A1.6 1.6 0 0 1 15 5.6V7",
    "M3.5 12.5h17",
  ],
  /** Kunden — ein Mensch, keine Menge: Gesucht wird immer einer. */
  kunden: [kreis(12, 8.4, 3.4), "M5 20c.9-3.6 3.6-5.6 7-5.6s6.1 2 7 5.6"],
  /** Preis — das Preisschild mit dem Loch für die Schnur. */
  preis: [
    "M3.5 4.5a1 1 0 0 1 1-1h6.7a2 2 0 0 1 1.4.6l7.3 7.3a2 2 0 0 1 0 2.8l-6.2 6.2a2 2 0 0 1-2.8 0L3.6 13.1a2 2 0 0 1-.1-.7Z",
    kreis(7.8, 7.8, 1.3),
  ],
  /** Angebot — das Blatt mit der geknickten Ecke. */
  angebot: [
    "M13.5 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8.5Z",
    "M13.5 3.5v5h5", "M8.8 13h6.4", "M8.8 16.5h4.4",
  ],
  /** Aufgaben — abgehakt und noch nicht abgehakt. */
  aufgaben: [
    "m3.5 7.6 1.9 1.9 3.4-3.4", "m3.5 16.6 1.9 1.9 3.4-3.4",
    "M12.5 8h8", "M12.5 17h8",
  ],
  /** Notiz — der Stift. */
  notiz: [
    "m4 20 .7-3.4a1 1 0 0 1 .27-.5L15.7 5.4a1.5 1.5 0 0 1 2.1 0l.8.8a1.5 1.5 0 0 1 0 2.1L8.1 18.8a1 1 0 0 1-.5.28Z",
    "m14.4 6.7 2.9 2.9",
  ],
  /** Mehr — drei Punkte. Runde Enden machen aus dem kurzen Strich einen Punkt. */
  mehr: ["M5.6 12h.01", "M12 12h.01", "M18.4 12h.01"],
  /** Suchen — die schlichte Lupe. Sie steht im Kopf am Knopf der globalen
      Suche und noch einmal im Feld des Suchfensters (`schale/Suche.tsx`). */
  suche: LUPE,
  /** Nachschlagen — dieselbe Lupe, mit dem Euro darin. */
  nachschlagen: [...LUPE, ...EURO],
  /** Große Ansicht — der Schirm auf dem Tisch. */
  ansicht: [
    "M3.5 6.5A1.5 1.5 0 0 1 5 5h14a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 15H5a1.5 1.5 0 0 1-1.5-1.5Z",
    "M9 19h6", "M12 15v4",
  ],
  /** Abmelden — die Tür und der Weg hinaus. */
  abmelden: [
    "M11 4.5H6.5A1.5 1.5 0 0 0 5 6v12a1.5 1.5 0 0 0 1.5 1.5H11",
    "M13.5 12H21", "m17.5 8.5 3.5 3.5-3.5 3.5",
  ],
  /** Der Winkel nach rechts: „hier geht es weiter". */
  weiter: ["m9.5 6 6 6-6 6"],
  /* ── Der Einsatz (17.09.2026) ── Drei Zeichen für die Aktionsleiste und
     die Blätter des Einsatzschirms. Derselbe Strich wie oben. */
  /** Kamera — der Körper mit dem Sucherbuckel und die Linse. */
  kamera: [
    "M4.5 8A1.5 1.5 0 0 1 6 6.5h2.2l1.4-2h4.8l1.4 2H18A1.5 1.5 0 0 1 19.5 8v10A1.5 1.5 0 0 1 18 19.5H6A1.5 1.5 0 0 1 4.5 18Z",
    kreis(12, 12.8, 3.4),
  ],
  /** Route — zwei Punkte und der Weg dazwischen. */
  /** Lkw — wir fahren (Liefern/Abholen im Kalender, 08.10.2026). */
  lkw: [
    "M2.8 6.5h10.7v9.6H2.8Z", "M13.5 9.6h3.9l3 3.3v3.2h-6.9",
    kreis(6.8, 17.6, 1.7), kreis(16.9, 17.6, 1.7),
  ],
  route: [kreis(6, 18, 2), kreis(18, 6, 2), "M8 18h6.5a3.5 3.5 0 0 0 0-7h-5a3.5 3.5 0 0 1 0-7H16"],
  /** Telefon — der Hörer. */
  telefon: [
    "M6.2 3.8h2.6l1.4 4-2 1.3a10.5 10.5 0 0 0 6.7 6.7l1.3-2 4 1.4v2.6a1.7 1.7 0 0 1-1.8 1.7A15.5 15.5 0 0 1 4.5 5.6a1.7 1.7 0 0 1 1.7-1.8Z",
  ],
  /* Der Briefumschlag — die dritte Tür zum Kunden neben Anrufen und Route.
     Er fehlte, solange niemand danach gefragt hat; die Griffreihe der
     Kundenakte (Telefon · Ort · Mail) braucht ihn. Rechteck und Klappe als
     zwei Striche, damit er bei 20 px nicht zuläuft. */
  mail: [
    "M3.5 6.5h17v11h-17z",
    "M3.5 7 12 13l8.5-6",
  ],
  /* ── Die Glocke (24.09.2026, 51-meldungen.md) ── Sie steht im Kopf neben
     Suche und Nachschlagen: was sich für mich geändert hat. Der Körper als
     ein Strich, der Klöppel als Bogen darunter. */
  glocke: [
    "M6.5 16.5V11a5.5 5.5 0 0 1 11 0v5.5l1.5 2h-14Z",
    "M10 20.5a2 2 0 0 0 4 0",
  ],
  /* ── Beantwortet (18.09.2026) ── Luca über die Vorbereitung: *„Bei
     Vorbereitung sieht dieser Haken optisch hässlich aus. Da muss ein anderer
     Haken rein."* Dort stand ein Schriftzeichen (✓/✗) mitten im Satz — es
     nimmt die Schriftart des Systems, sitzt auf keiner Grundlinie mit dem Wort
     daneben und ist auf jedem Telefon anders breit. Jetzt sind es zwei
     Zeichnungen aus diesem Satz: derselbe Strich wie überall, `currentColor`,
     also grün am „Ja" und rot am Mangel, ohne dass hier eine Farbe steht. */
  /** Haken — beantwortet, erledigt, stimmt. */
  haken: ["m4.5 12.6 4.6 4.6L19.5 6.8"],
  /** Kreuz — verneint. Kein „Fehler": Ein „Nein" ist eine Antwort. */
  kreuz: ["M6.4 6.4l11.2 11.2", "M17.6 6.4 6.4 17.6"],
  /** Plus — in die Sammlung legen (19.09.2026, „Merken" an der Trefferzeile
   *  am Telefon). Dieselbe Zeichnung wie im Anlegekreis der Leiste
   *  (`schale/Handyleiste.tsx`), damit „dazu" überall gleich aussieht. */
  plus: ["M12 5.5v13", "M5.5 12h13"],
  /* ── Der Einsatz, nachgeschärft (18.09.2026) ── Luca: *„Designt diese
     Knöpfe ein bisschen schicker."* Jeder Infoknopf und jede Nebenaktion des
     Einsatzschirms trägt ein Zeichen vor dem Wort — mit Handschuh trifft
     das Auge die Fläche, bevor es das Wort liest. */
  /** Einsatzort — die Stecknadel. */
  ort: ["M12 20.5s-6.2-5.4-6.2-10.6a6.2 6.2 0 0 1 12.4 0c0 5.2-6.2 10.6-6.2 10.6Z", kreis(12, 9.9, 2.3)],
  /** Navigation — der Pfeil, der losfährt. */
  navigation: ["m4.5 11.2 15-6.7-6.7 15-1.9-6.4Z"],
  /** Pause — zwei Balken. */
  pause: ["M9 6.5v11", "M15 6.5v11"],
  /** Weiter — das Dreieck, das wieder anläuft. */
  abspielen: ["M8.5 6.2v11.6l9.3-5.8Z"],
  /** Verlauf — die Uhr mit dem Pfeil zurück. */
  verlauf: ["M4.6 12a7.4 7.4 0 1 0 2.2-5.3", "M4.4 4.6v3.3h3.3", "M12 8.2v4l2.7 1.7"],
  /* Derselbe Bogen wie beim Verlauf, ohne die Zeiger: zurueck auf Anfang,
     nicht zurueck in der Zeit (20.09.2026, „Zuruecksetzen" beim Vermieten). */
  zuruecksetzen: ["M4.6 12a7.4 7.4 0 1 0 2.2-5.3", "M4.4 4.6v3.3h3.3"],
  /** Schaden — das Warndreieck. */
  schaden: ["M12 4.2 20.8 19.3H3.2Z", "M12 10v4.3", "M12 16.9h.01"],
  /** Bild — das Foto aus der Galerie: Rahmen, Berg und Sonne. */
  bild: [
    "M4 6A1.5 1.5 0 0 1 5.5 4.5h13A1.5 1.5 0 0 1 20 6v12a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18Z",
    "m4.5 16.5 4.5-4.5 4 4 2.5-2.5 4 4", kreis(15.5, 9, 1.4),
  ],
  /* ── Die Maschine (19.09.2026) ── Für den Kreis in der Leiste: Luca,
     *„‚Maschinen anlegen' ist in dem Fall dann hinter dem Plus-Button"*.
     Das Blatt dahinter zeigt je Griff ein Zeichen, und für das Gerät gab es
     keins. Ein Radlader von der Seite — zwei Räder, Kabine, Arm und
     Schaufel —, weil er bei 20 px als Maschine lesbar bleibt; ein Bagger
     mit Kette lief in der Probe zu einem Klecks unten rechts zusammen. */
  /** Maschine — der Radlader: Räder, Kabine, Arm, Schaufel. */
  maschine: [
    kreis(7, 16.9, 2.9), kreis(16.3, 16.9, 2.9),
    "M3.6 15.2v-3.5h13.7", "M9.9 16.9h3.5",
    "M5.3 11.7V5.9a1 1 0 0 1 1-1h3.9a1 1 0 0 1 1 1v5.8",
    "m13.6 11.7 4-4.4", "M17 6.4h3.6v4.9l-2.6 1.3",
  ],
  /* ── Das Herunterladen (19.09.2026) ── Luca über die Belege am Auftrag:
     *„Das muss einfach nur zum Runterladen sein."* Der Pfeil auf die Ablage
     sagt das ohne Wort; das Wort steht im `aria-label` der Zeile. */
  /** Laden — der Pfeil nach unten auf die Ablage. */
  laden: ["M12 4.5v10.5", "m7.5 10.5 4.5 4.5 4.5-4.5", "M5 19.5h14"],
  /* ── Das Öffnen (06.10.2026) ── Luca: *„Mach doch einfach nur ‚Beleg
     öffnen'."* Ein Beleg geht seitdem in einen eigenen Reiter statt in den
     Download-Ordner — das Blatt mit dem Pfeil nach oben rechts hinaus. */
  /** Öffnen — das Blatt, der Pfeil heraus. */
  oeffnen: ["M13.5 4.5h6v6", "M19.5 4.5 11 13", "M17.5 13.5v5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1h5"],
  /* ── Der Filter (19.09.2026) ── Luca über Vermieten und Nachschlagen:
     *„Mach einfach die Suchleiste so, wie sie ist, und dann hast du rechts
     daneben so einen Filterbutton."* Der Trichter, weil er bei 20 px ohne
     Wort „einschränken" sagt; drei Regler lesen sich eher als Einstellungen. */
  /** Filter — der Trichter. */
  filter: ["M4.2 5.2h15.6l-6.1 7.3v5.6l-3.4 1.9v-7.5Z"],
  /* ── Die Hülle „Signal" (07.10.2026, docs/64, Paket 3) ───────────────
     Seit Signal trägt die Seitenleiste je Punkt ein Zeichen vor dem Wort —
     so wie das Muster (app-design-muster, Schreibtisch, Hülle). Das Wort
     bleibt; das Zeichen ist der Griff für die eingeklappte Leiste (56 px),
     in der nur noch es steht. Gezeichnet nach dem Sollbild
     (docs/artefakte/2026-10-07-signal), Kreise und Rechtecke als Pfade. */
  /** Menü — die drei Striche (Seitenleiste ein- und ausklappen). */
  menue: ["M4 6h16M4 12h16M4 18h16"],
  /** Blitz — der schnelle Weg: gleich vermieten. */
  blitz: ["M13 2 3 14h9l-1 8 10-12h-9l1-8z"],
  /** Planung — der Wandkalender mit Einträgen. */
  planung: ["M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-13a2 2 0 0 1 2 -2Z", "M3 9h18M8 2v4M16 2v4M7 13h4M13 17h4"],
  /** Vermieten — das Gerät auf Rädern. */
  vermieten: ["M3 17h2l2-6h6l3 6h3", "M5 18a2 2 0 1 0 4 0a2 2 0 1 0 -4 0", "M15 18a2 2 0 1 0 4 0a2 2 0 1 0 -4 0", "M13 11V6h4l2 5"],
  /** Auftrag — das Klemmbrett. */
  auftrag: ["M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2Z", "M9 3v3h6V3M9 11h6M9 15h4"],
  /** Ressourcen — der Bagger. */
  ressource: ["M2 17h5l3-8h5l4 8h3", "M10 9 8 4H4", "M4 18a2 2 0 1 0 4 0a2 2 0 1 0 -4 0", "M16 18a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"],
  /** Weiteres — vier Kacheln. */
  weiteres: ["M4.5 3h4a1.5 1.5 0 0 1 1.5 1.5v4a1.5 1.5 0 0 1 -1.5 1.5h-4a1.5 1.5 0 0 1 -1.5 -1.5v-4a1.5 1.5 0 0 1 1.5 -1.5Z", "M15.5 3h4a1.5 1.5 0 0 1 1.5 1.5v4a1.5 1.5 0 0 1 -1.5 1.5h-4a1.5 1.5 0 0 1 -1.5 -1.5v-4a1.5 1.5 0 0 1 1.5 -1.5Z", "M4.5 14h4a1.5 1.5 0 0 1 1.5 1.5v4a1.5 1.5 0 0 1 -1.5 1.5h-4a1.5 1.5 0 0 1 -1.5 -1.5v-4a1.5 1.5 0 0 1 1.5 -1.5Z", "M15.5 14h4a1.5 1.5 0 0 1 1.5 1.5v4a1.5 1.5 0 0 1 -1.5 1.5h-4a1.5 1.5 0 0 1 -1.5 -1.5v-4a1.5 1.5 0 0 1 1.5 -1.5Z"],
  /** Abrechnung — der Kassenzettel. */
  abrechnung: ["M4 2v20l3-2 3 2 2-2 2 2 3-2 3 2V2l-3 2-3-2-2 2-2-2-3 2z", "M8 9h8M8 13h6"],
  /** Belege — zwei Blätter übereinander. */
  belege: ["M8 3h9a2 2 0 0 1 2 2v14", "M6 7h8a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2Z"],
  /** Einstellungen — das Zahnrad. */
  zahnrad: ["M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z", "M9 12a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"],
  /** Aktualisieren — der Pfeil im Kreis. */
  aktualisieren: ["M21 12a9 9 0 1 1-2.6-6.4L21 8", "M21 3v5h-5"],
  /** Hilfe — das Fragezeichen (öffnet das Handbuch). */
  hilfe: ["M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0", "M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01"],
  /* Ein Hinweis, keine Frage — der Abrechnungssatz unter der Auskunft (08.10.2026). */
  info: ["M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0", "M12 11v5.5", "M12 7.6h.01"],
  /** Warnung — das Dreieck mit Ausrufezeichen. */
  warnung: ["M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z", "M12 9v4M12 17h.01"],
  /** Heute in der Seitenleiste — die Sonne (am Telefon bleibt der Kalender). */
  sonne: ["M8 12a4 4 0 1 0 8 0a4 4 0 1 0 -8 0", "M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"],
  /** Preisstamm — das Preisschild. */
  preisschild: ["M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z", "M6 7.5a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0"],
} as const;

export type Zeichenname = keyof typeof PFADE;

/**
 * Ein Zeichen aus dem Satz. Immer 24 px, immer `currentColor`.
 *
 * Es trägt keine Beschriftung und bekommt keine: Wer es benutzt, setzt
 * das Wort daneben oder — wenn wirklich keins danebensteht — ein
 * `aria-label` an die Fläche darum.
 */
export function Zeichen({ name }: { name: Zeichenname }) {
  return (
    <svg
      className="zeichen"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PFADE[name].map((d) => <path key={d} d={d} />)}
    </svg>
  );
}
